import { createApp } from 'vue';
import axios from 'axios';
import './registerServiceWorker';

// Core plugins for your app
import App from './App.vue';
import router from './router';
import store from './store'; // Your Vuex 4 store instance
import vuetify from './plugins/vuetify';
import i18n from '@/plugins/i18n'; // Ensure this is configured for Vue 3 (vue-i18n v9+)

// Style and library imports
import 'roboto-fontface/css/roboto/roboto-fontface.css';
import '@mdi/font/css/materialdesignicons.css';
import './main.css';
import './transitions.css';
import FlagIcon from 'vue-flag-icon';
import Rollbar from 'rollbar';

// Import your refactored, store-independent filter functions
import allFilters from '../components/utils/filter.js';

// --- Create the Vue 3 App Instance ---
import { setApp } from './app.js';
import { createPinia } from 'pinia';
import { UserApiService } from './rennovation/common/services/api/userApiService.js';
import { DutyApiService } from './rennovation/common/services/api/dutyApiService.js';
import { AgreementApiService } from './rennovation/common/services/api/agreementApiService.js';
import { ExchangeApiService } from './rennovation/common/services/api/exchangeApiService.js';
import { ReportApiService } from './rennovation/common/services/api/reportApiService.js';
import { DayApiService } from './rennovation/common/services/api/dayApiService.js';
import { PermissionService } from './rennovation/common/services/domain/permissionService.js';
import { RosterApiService } from './rennovation/common/services/api/rosterApiService.js';
import { ManualBalanceApiService } from './rennovation/common/services/api/manualBalanceApiService.ts';
import { HistoryApiService } from './rennovation/common/services/api/historyApiService.js';
import { PreviewApiService } from './rennovation/common/services/api/previewApiService.js';
import { USER_API_KEY } from './rennovation/common/services/api/useUserApi.js';
import { DUTY_API_KEY } from './rennovation/common/services/api/useDutyApi.js';
import { DAY_API_KEY } from './rennovation/common/services/api/useDayApi.js';
import { EXCHANGE_API_KEY } from './rennovation/common/services/api/useExchangeApi.js';
import { AGREEMENT_API_KEY } from './rennovation/common/services/api/useAgreementApi.js';
import { USER_PERMISSION_KEY } from './rennovation/common/services/domain/useUserPermission.js';
import { REPORT_API_KEY } from './rennovation/common/services/api/useReportApi.js';
import { HISTORY_API_KEY } from './rennovation/common/services/api/useHistoryApi.js';
import { ROSTER_API_KEY } from './rennovation/common/services/api/useRosterApi.js';
import { MANUALBALANCE_API_KEY } from './rennovation/common/services/api/useManualBalanceApiService.js';
import { ABSENCE_API_KEY } from './rennovation/common/services/api/useAbsenceApi.js';
import { AbsenceApiService } from './rennovation/common/services/api/absenceApiService.js';
import { PREVIEW_API_KEY } from './rennovation/common/services/api/usePreviewApi.ts';

const app = createApp(App);
setApp(app);

// --- BREAKING CHANGE: Global Filters are Removed ---
// The recommended replacement is `app.config.globalProperties`.
// This makes them available in any component's template as `$filters.formatDate(...)`
const filters = {
	...allFilters,
	secondsToMinutes(time) {
		if (time === null || time === undefined || time === 0) {
			return 0;
		}
		return Math.round(time / 60);
	},
};
app.config.globalProperties.$filters = filters;

axios.defaults.headers.common['Content-Type'] = 'application/json';

// --- Rollbar Integration for Vue 3 ---
const ROLLBAR_ENABLED = false;
if (import.meta.env.VITE_ROLLBAR_TOKEN && ROLLBAR_ENABLED) {
	console.log('Rollbar token initiated for web');
	const rollbar = new Rollbar({
		accessToken: import.meta.env.VITE_ROLLBAR_TOKEN,
		captureUncaught: true,
		captureUnhandledRejections: true,
		environment: import.meta.env.VITE_STAGE,
		payload: {
			code_version: import.meta.env.VITE_VERSION,
			service: 'web',
			commit: import.meta.env.VITE_COMMIT,
			tenant: localStorage.getItem('tenant'),
		},
	});

	app.config.globalProperties.$rollbar = rollbar;

	app.config.errorHandler = (err, instance, info) => {
		rollbar.error(err);
		throw err; // rethrow
	};

	console.log(`Connected to Rollbar on stage ${import.meta.env.VITE_STAGE}`);
}

app.provide(USER_API_KEY, new UserApiService());
app.provide(USER_PERMISSION_KEY, new PermissionService());
app.provide(DUTY_API_KEY, new DutyApiService());
app.provide(DAY_API_KEY, new DayApiService());
app.provide(AGREEMENT_API_KEY, new AgreementApiService());
app.provide(EXCHANGE_API_KEY, new ExchangeApiService());
app.provide(REPORT_API_KEY, new ReportApiService());
app.provide(ROSTER_API_KEY, new RosterApiService());
app.provide(MANUALBALANCE_API_KEY, new ManualBalanceApiService());
app.provide(HISTORY_API_KEY, new HistoryApiService());
app.provide(PREVIEW_API_KEY, new PreviewApiService());
app.provide(ABSENCE_API_KEY, new AbsenceApiService());

// --- Register Plugins ---
const pinia = createPinia();

app.use(pinia);
app.use(store); // Use the Vuex 4 store
app.use(router);
app.use(vuetify);
app.use(i18n);
app.use(FlagIcon);

app.mount('#app');
